import React, { useState } from 'react';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useUserData, useLogout } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAds } from '../contexts/AdContext';
import './AccountSettings.css';

/**
 * Issue #137: Account Settings Page
 * Displays profile information and account management options
 * Design follows LanguageSettings pattern for consistency
 */
function AccountSettings() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const { t } = useLanguage();
  const { consentGiven, grantConsent, revokeConsent } = useAds();

  // Handle toggle for personalized ads
  const handleAdsToggle = () => {
    if (consentGiven) {
      revokeConsent();
    } else {
      grantConsent();
    }
  };

  // Format the account creation date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="account-settings-page">
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-account">
        <span className="tab-title">{t('accountSettings.title')}</span>
      </div>

      {/* Account Settings Content */}
      <main className="account-settings-content">
        {/* Back Button */}
        <BackButton
          onClick={navigation.onNavigateToProfile}
          className="back-button-content"
        />

        {/* Profilinformationen Section */}
        <div className="settings-section" role="region" aria-labelledby="profile-info-section-title">
          <h2 className="settings-section-title" id="profile-info-section-title">{t('accountSettings.profileInfo')}</h2>
          <div className="profile-info-content">
            <div className="profile-info-row">
              <span className="profile-info-text">
                {t('accountSettings.name')} : {userData?.first_name} {userData?.last_name}
              </span>
            </div>
            <div className="profile-info-row">
              <span className="profile-info-text">
                {t('accountSettings.email')} : {userData?.email}
              </span>
            </div>
            <div className="profile-info-row">
              <span className="profile-info-text">
                {t('accountSettings.university')} : {userData?.enrolled_university || '-'}
              </span>
            </div>
            <div className="profile-info-row">
              <span className="profile-info-text">
                {t('accountSettings.accountStatus')} : {t('accountSettings.accountStatusActive')} {formatDate(userData?.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Issue #268: Personalized ads toggle */}
        <div className="settings-section" role="region" aria-labelledby="ads-section-title">
          <h2 className="settings-section-title" id="ads-section-title">{t('ads.settings.sectionTitle')}</h2>
          <div className="ads-toggle-container">
            <label className="ads-toggle" aria-label={t('ads.settings.toggleLabel')}>
              <input
                type="checkbox"
                checked={consentGiven === true}
                onChange={handleAdsToggle}
              />
              <span className="ads-toggle-switch" aria-hidden="true"></span>
              <span className="ads-toggle-text">{t('ads.settings.toggleLabel')}</span>
            </label>
          </div>
        </div>

        {/* Kontoverwaltung Section */}
        <div className="settings-section" role="region" aria-labelledby="account-management-section-title">
          <h2 className="settings-section-title" id="account-management-section-title">{t('accountSettings.accountManagement')}</h2>
          <div className="settings-options">
            <button
              className="settings-item-row"
              onClick={navigation.onNavigateToDeleteAccount}
              type="button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="settings-item-text">{t('accountSettings.deleteAccount')}</span>
            </button>
          </div>
        </div>
      </main>

      <BottomNavigation
        activeTab="profile"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default AccountSettings;
